import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit, Trash2, Shield, X, Check, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface Role {
  id: number;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

interface RoleFormData {
  name: string;
  description: string;
}

interface PermissionCatalogEntry {
  action: string;
  module: string;
  label: string;
  description: string;
  frontendOnly?: boolean;
}

export default function Roles() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [catalogByModule, setCatalogByModule] = useState<Record<string, PermissionCatalogEntry[]>>({});
  const [infoDialogPermission, setInfoDialogPermission] = useState<PermissionCatalogEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showPermissionsForm, setShowPermissionsForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [managePermissionsRole, setManagePermissionsRole] = useState<Role | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const [newRole, setNewRole] = useState<RoleFormData>({
    name: '',
    description: '',
  });

  const [editRole, setEditRole] = useState<RoleFormData>({
    name: '',
    description: '',
  });

  // Load roles and permission catalog
  useEffect(() => {
    loadRoles();
    loadPermissionsCatalog();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/roles', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar roles');
      }

      const data = await response.json();
      setRoles(data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al cargar roles');
      console.error('Load roles error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissionsCatalog = async () => {
    try {
      const response = await fetch('/api/roles/permissions/catalog', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar catálogo de permisos');
      }

      const data = await response.json();
      setCatalogByModule(data.byModule);
    } catch (error) {
      console.error('Load permissions catalog error:', error);
    }
  };

  // Create new role
  const handleCreateRole = async () => {
    setCreateLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newRole),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear rol');
      }

      setShowCreateForm(false);
      setNewRole({ name: '', description: '' });
      loadRoles();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al crear rol');
    } finally {
      setCreateLoading(false);
    }
  };

  // Update role
  const handleEditRole = async () => {
    if (!editingRole) return;

    setEditLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/roles/${editingRole.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(editRole),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar rol');
      }

      setShowEditForm(false);
      setEditingRole(null);
      loadRoles();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al actualizar rol');
    } finally {
      setEditLoading(false);
    }
  };

  // Delete role
  const handleDeleteRole = async (id: number) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este rol?')) {
      return;
    }

    try {
      setError(null);
      const response = await fetch(`/api/roles/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar rol');
      }

      loadRoles();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al eliminar rol');
    }
  };

  // Update role permissions
  const handleUpdatePermissions = async () => {
    if (!managePermissionsRole) return;

    setPermissionsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/roles/${managePermissionsRole.id}/permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ permissions: selectedPermissions }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar permisos');
      }

      setShowPermissionsForm(false);
      setManagePermissionsRole(null);
      setSelectedPermissions([]);
      loadRoles();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al actualizar permisos');
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Open edit form
  const openEditForm = (role: Role) => {
    setEditingRole(role);
    setEditRole({
      name: role.name,
      description: role.description || '',
    });
    setShowEditForm(true);
  };

  // Open permissions management form
  const openPermissionsForm = (role: Role) => {
    setManagePermissionsRole(role);
    setSelectedPermissions(role.permissions || []);
    setShowPermissionsForm(true);
  };

  // Toggle permission selection
  const togglePermission = (permission: string) => {
    if (selectedPermissions.includes(permission)) {
      setSelectedPermissions(selectedPermissions.filter(p => p !== permission));
    } else {
      setSelectedPermissions([...selectedPermissions, permission]);
    }
  };

  // Toggle all permissions for a module
  const toggleResourcePermissions = (actions: string[]) => {
    const allSelected = actions.every(p => selectedPermissions.includes(p));

    if (allSelected) {
      setSelectedPermissions(selectedPermissions.filter(p => !actions.includes(p)));
    } else {
      const newPermissions = [...selectedPermissions];
      actions.forEach(p => {
        if (!newPermissions.includes(p)) {
          newPermissions.push(p);
        }
      });
      setSelectedPermissions(newPermissions);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Gestión de Roles y Permisos</h1>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Rol
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Roles del Sistema</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando roles...</div>
          ) : (
            <div className="w-full">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">Nombre</th>
                    <th className="text-left p-3">Descripción</th>
                    <th className="text-left p-3">Permisos</th>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-right p-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map(role => (
                    <tr key={role.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{role.name}</td>
                      <td className="p-3 text-sm text-gray-600">
                        {role.description || 'Sin descripción'}
                      </td>
                      <td className="p-3 text-sm">
                        <span className="text-blue-600">{role.permissions.length} permisos</span>
                      </td>
                      <td className="p-3">
                        {role.isSystem ? (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                            Sistema
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                            Personalizado
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openPermissionsForm(role)}
                          >
                            <Shield className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditForm(role)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteRole(role.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Role Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Crear Nuevo Rol</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Nombre del Rol *
                </label>
                <Input
                  value={newRole.name}
                  onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                  placeholder="Ej: Cajero, Vendedor, etc."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Descripción
                </label>
                <Input
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                  placeholder="Descripción del rol"
                />
              </div>

              <p className="text-sm text-gray-600">
                Después de crear el rol, podrás asignar permisos específicos.
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateRole} disabled={createLoading}>
                {createLoading ? 'Creando...' : 'Crear Rol'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditForm && editingRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">
              Editar Rol - {editingRole.name}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Nombre del Rol *
                </label>
                <Input
                  value={editRole.name}
                  onChange={(e) => setEditRole({ ...editRole, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Descripción
                </label>
                <Input
                  value={editRole.description}
                  onChange={(e) => setEditRole({ ...editRole, description: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowEditForm(false)}>
                Cancelar
              </Button>
              <Button onClick={handleEditRole} disabled={editLoading}>
                {editLoading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Permissions Modal */}
      {showPermissionsForm && managePermissionsRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                Permisos de {managePermissionsRole.name}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPermissionsForm(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Selecciona los permisos que tendrá este rol. Los permisos están organizados por módulo. Pasa el cursor sobre un permiso o haz clic en el ícono de información para ver qué hace.
              </p>
            </div>

            <div className="space-y-4">
              {Object.entries(catalogByModule).map(([moduleName, entries]) => {
                const actions = entries.map(e => e.action);
                return (
                  <div key={moduleName} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold">{moduleName}</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleResourcePermissions(actions)}
                      >
                        {actions.every(a => selectedPermissions.includes(a))
                          ? 'Deseleccionar Todo'
                          : 'Seleccionar Todo'}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {entries.map(entry => (
                        <div key={entry.action} className="flex items-center gap-1 hover:bg-gray-50 p-2 rounded">
                          <label className="flex items-center gap-2 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              checked={selectedPermissions.includes(entry.action)}
                              onChange={() => togglePermission(entry.action)}
                              className="rounded"
                            />
                            <span className="text-sm" title={entry.description}>{entry.label}</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => setInfoDialogPermission(entry)}
                            className="text-gray-400 hover:text-indigo-600 shrink-0"
                            aria-label={`Más información sobre ${entry.label}`}
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <div className="text-sm text-gray-600">
                {selectedPermissions.length} permisos seleccionados
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPermissionsForm(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleUpdatePermissions} disabled={permissionsLoading}>
                  <Check className="mr-2 h-4 w-4" />
                  {permissionsLoading ? 'Guardando...' : 'Guardar Permisos'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permission Info Dialog */}
      <Dialog open={infoDialogPermission !== null} onOpenChange={(open) => !open && setInfoDialogPermission(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{infoDialogPermission?.label}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700">{infoDialogPermission?.description}</p>
          {infoDialogPermission?.frontendOnly && (
            <p className="text-xs text-amber-600 mt-2">
              Este permiso solo controla qué se ve en el menú — no bloquea ninguna acción del lado del servidor.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
